import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import axios from 'axios';
import {
    Button,
    Checkbox,
    Empty,
    Input,
    InputNumber,
    Modal,
    QRCode,
    Space,
    Typography,
    message
} from 'antd';
import {
    CheckCircleOutlined,
    QrcodeOutlined,
    ReloadOutlined,
    RollbackOutlined,
    SwapOutlined
} from '@ant-design/icons';
import Layout from './Layout';
import InstallerOutboxBadge from './InstallerOutboxBadge';
import InstallerOfflineBanner from './InstallerOfflineBanner';
import {
    canScanIssueQr,
    parseIssueCodeFromScan,
    scanIssueQrNative
} from '../utils/installerBarcode';
import {
    enqueueTmcIssueAccept,
    enqueueTmcPeerAccept,
    enqueueTmcPeerOffer
} from '../utils/installerOfflineQueue';
import {
    getInstallerUser,
    loadCachedTmc,
    saveCachedTmc
} from '../utils/installerTaskCache';
import {
    applyAcceptToHoldings,
    applyOfferToHoldings,
    buildTransferPayload,
    makeTransferId,
    parseTransferQr
} from '../utils/installerTmcQr';
import '../styles/InstallerWorkHub.css';
import '../styles/InstallerTmcPage.css';

const { Text, Title } = Typography;

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
});

const qtyLabel = row =>
    `${row.quantity} ${row.item?.unit || row.unit || ''}`.trim();

const InstallerTmcPage = () => {
    const [nativeScan, setNativeScan] = useState(false);
    const [loading, setLoading] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [accepting, setAccepting] = useState(false);
    const [pendingIssues, setPendingIssues] = useState([]);
    const [holdings, setHoldings] = useState([]);
    const [preview, setPreview] = useState(null);
    const [returnRow, setReturnRow] = useState(null);
    const [returnQty, setReturnQty] = useState(1);
    const [returnNote, setReturnNote] = useState('');
    const [fromCache, setFromCache] = useState(false);
    const [transferOpen, setTransferOpen] = useState(false);
    const [transferQty, setTransferQty] = useState({});
    const [transferSelected, setTransferSelected] = useState({});
    const [qrOpen, setQrOpen] = useState(false);
    const [qrValue, setQrValue] = useState('');
    const [incoming, setIncoming] = useState(null);
    const [acceptingTransfer, setAcceptingTransfer] = useState(false);

    useEffect(() => {
        setNativeScan(canScanIssueQr());
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [issuesRes, holdingsRes] = await Promise.all([
                axios.get('/api/tmc/my/issues', {
                    headers: authHeaders()
                }),
                axios.get('/api/tmc/my', {
                    headers: authHeaders()
                })
            ]);
            setPendingIssues(issuesRes.data.data || []);
            setHoldings(holdingsRes.data.data || []);
            setFromCache(false);
            saveCachedTmc({
                holdings: holdingsRes.data.data || [],
                pendingIssues: issuesRes.data.data || []
            }).catch(() => {});
        } catch (error) {
            const cached = await loadCachedTmc();
            if (cached) {
                setPendingIssues(cached.pendingIssues || []);
                setHoldings(cached.holdings || []);
                setFromCache(true);
                message.warning(
                    'Нет сети — показан сохранённый список ТМЦ'
                );
            } else {
                message.error(
                    error.response?.data?.message ||
                        error.message ||
                        'Не удалось загрузить ТМЦ'
                );
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const held = useMemo(
        () => holdings.filter(r => r.status === 'held'),
        [holdings]
    );
    const onObject = useMemo(
        () => holdings.filter(r => r.status === 'on_object'),
        [holdings]
    );
    const pendingReturn = useMemo(
        () => holdings.filter(r => r.status === 'return_pending'),
        [holdings]
    );
    const pendingTransfer = useMemo(
        () => holdings.filter(r => r.status === 'transfer_pending'),
        [holdings]
    );

    const openPreview = issue => {
        if (!issue) return;
        setPreview(issue);
    };

    const lookupAndOpen = async raw => {
        const transfer = parseTransferQr(raw);
        if (transfer) {
            const me = getInstallerUser();
            if (Number(transfer.fromUserId) === Number(me?.id)) {
                message.warning(
                    'Это ваш QR. Его должен отсканировать коллега'
                );
                return;
            }
            setIncoming(transfer);
            return;
        }

        const code = parseIssueCodeFromScan(raw);
        if (!code) {
            message.error('В QR нет выдачи или передачи ТМЦ');
            return;
        }
        try {
            const { data } = await axios.post(
                '/api/tmc/my/issues/lookup',
                { qr: raw, code },
                { headers: authHeaders() }
            );
            openPreview(data.data);
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    error.message ||
                    'Выдача не найдена'
            );
        }
    };

    const handleScan = async () => {
        if (!nativeScan) {
            message.info(
                'Сканер QR работает в приложении монтажника'
            );
            return;
        }
        setScanning(true);
        try {
            const result = await scanIssueQrNative();
            if (result.cancelled || !result.value) return;
            await lookupAndOpen(result.value);
        } catch (error) {
            message.error(
                error?.message || 'Не удалось открыть сканер'
            );
        } finally {
            setScanning(false);
        }
    };

    const handleAccept = async issue => {
        const code = issue?.code;
        if (!code) return;
        setAccepting(true);
        try {
            const result = await enqueueTmcIssueAccept({
                code,
                qr: code
            });
            if (result.queued) {
                message.warning(
                    'Нет сети — подтверждение в очереди, пока не уйдёт на сервер'
                );
            } else {
                message.success('ТМЦ принято. Теперь оно на вас');
            }
            setPreview(null);
            await load();
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    error.message ||
                    'Не удалось подтвердить'
            );
        } finally {
            setAccepting(false);
        }
    };

    const persistHoldings = next => {
        setHoldings(next);
        saveCachedTmc({
            holdings: next,
            pendingIssues
        }).catch(() => {});
    };

    const openTransferSheet = () => {
        const qty = {};
        const selected = {};
        held.forEach(row => {
            qty[row.id] = Number(row.quantity);
            selected[row.id] = false;
        });
        setTransferQty(qty);
        setTransferSelected(selected);
        setTransferOpen(true);
    };

    const buildSelectedLines = () =>
        held
            .filter(row => transferSelected[row.id])
            .map(row => ({
                holding_id: row.id,
                item_id: Number(row.item_id || row.item?.id),
                quantity: Number(transferQty[row.id] || 0),
                name: row.item?.name,
                unit: row.item?.unit,
                sku: row.item?.sku
            }))
            .filter(line => line.item_id && line.quantity > 0);

    const createTransferQr = async () => {
        const lines = buildSelectedLines();
        if (!lines.length) {
            message.warning('Отметьте позиции и количество');
            return;
        }
        for (const line of lines) {
            const row = held.find(h => h.id === line.holding_id);
            if (row && line.quantity > Number(row.quantity) + 0.0001) {
                message.error(
                    `Нельзя передать больше, чем есть: ${row.item?.name}`
                );
                return;
            }
        }

        const user = getInstallerUser();
        const transferId = makeTransferId();
        const payload = {
            transfer_id: transferId,
            from_user_id: Number(user?.id),
            role: 'offer',
            lines
        };

        try {
            const qr = buildTransferPayload({
                transferId,
                fromUser: user,
                lines
            });
            const result = await enqueueTmcPeerOffer(payload);
            persistHoldings(
                applyOfferToHoldings(holdings, { transferId, lines })
            );
            setTransferOpen(false);
            setQrValue(qr);
            setQrOpen(true);
            if (result.queued) {
                message.warning(
                    'Нет сети — передача в очереди. QR всё равно можно показать коллеге'
                );
            }
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    error.message ||
                    'Не удалось создать передачу'
            );
        }
    };

    const acceptIncoming = async () => {
        if (!incoming) return;
        const me = getInstallerUser();
        setAcceptingTransfer(true);
        try {
            const result = await enqueueTmcPeerAccept({
                transfer_id: incoming.transferId,
                from_user_id: incoming.fromUserId,
                to_user_id: Number(me?.id),
                role: 'accept',
                lines: incoming.lines
            });
            persistHoldings(
                applyAcceptToHoldings(holdings, {
                    transferId: incoming.transferId,
                    lines: incoming.lines
                })
            );
            setIncoming(null);
            if (result.queued) {
                message.warning(
                    'Нет сети — приём в очереди и уйдёт на сервер сам'
                );
            } else {
                message.success('ТМЦ принято от коллеги');
                await load();
            }
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    error.message ||
                    'Не удалось принять передачу'
            );
        } finally {
            setAcceptingTransfer(false);
        }
    };

    const submitReturn = async () => {
        if (!returnRow) return;
        try {
            await axios.post(
                `/api/tmc/my/${returnRow.id}/return`,
                {
                    quantity: returnQty,
                    note: returnNote
                },
                { headers: authHeaders() }
            );
            message.success('Запрос на возврат отправлен');
            setReturnRow(null);
            setReturnNote('');
            load();
        } catch (error) {
            message.error(
                error.response?.data?.message || error.message
            );
        }
    };

    const renderHolding = (row, action) => (
        <div key={row.id} className="installer-tmc-item">
            <div className="installer-tmc-item__main">
                <div className="installer-tmc-item__name">
                    {row.item?.name || '—'}
                </div>
                <Text type="secondary" className="installer-tmc-item__meta">
                    {qtyLabel(row)}
                    {row.item?.sku ? ` · ${row.item.sku}` : ''}
                    {row.task?.external_id
                        ? ` · ${row.task.external_id}`
                        : ''}
                </Text>
            </div>
            {action}
        </div>
    );

    const canAccept =
        preview &&
        preview.status === 'pending' &&
        (preview.lines || []).length > 0;

    return (
        <Layout>
            <div className="installer-tmc-tools">
                <Space
                    direction="vertical"
                    style={{ width: '100%' }}
                    size={8}
                >
                    <InstallerOfflineBanner />
                    <InstallerOutboxBadge />
                </Space>
            </div>

            <div className="installer-work-hub installer-tmc-hub">
                <div className="installer-work-hub__header">
                    <div>
                        <Title
                            level={3}
                            className="installer-work-hub__title"
                        >
                            ТМЦ
                        </Title>
                        <Text
                            type="secondary"
                            className="installer-work-hub__subtitle"
                        >
                            Сканируйте QR склада или коллеги.
                            Своё ТМЦ можно отдать по QR — в журнал
                            всё запишется, без сети уйдёт в очередь.
                        </Text>
                    </div>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={load}
                        loading={loading}
                    />
                </div>

                {fromCache ? (
                    <div className="installer-tmc-empty" style={{ marginBottom: 12 }}>
                        Показан сохранённый список — нет сети
                    </div>
                ) : null}

                <button
                    type="button"
                    className="installer-tmc-scan"
                    onClick={handleScan}
                    disabled={scanning || !nativeScan}
                >
                    <QrcodeOutlined />
                    <span>
                        {scanning
                            ? 'Открываю камеру…'
                            : 'Сканировать QR'}
                    </span>
                    {!nativeScan ? (
                        <small>
                            Только в приложении, не в браузере
                        </small>
                    ) : (
                        <small>
                            Выдача со склада или передача от коллеги
                        </small>
                    )}
                </button>

                <button
                    type="button"
                    className="installer-tmc-scan installer-tmc-scan--transfer"
                    onClick={openTransferSheet}
                    disabled={!held.length}
                >
                    <SwapOutlined />
                    <span>Передать своё ТМЦ</span>
                    <small>QR для другого монтажника</small>
                </button>

                <section className="installer-tmc-section">
                    <div className="installer-tmc-section__title">
                        Ждут подтверждения
                        {pendingIssues.length > 0 ? (
                            <span className="installer-tmc-count">
                                {pendingIssues.length}
                            </span>
                        ) : null}
                    </div>
                    {pendingIssues.length ? (
                        pendingIssues.map(issue => (
                            <button
                                key={issue.id}
                                type="button"
                                className="installer-work-card installer-tmc-issue"
                                onClick={() => openPreview(issue)}
                            >
                                <div className="installer-work-card__top">
                                    <div className="installer-work-card__title">
                                        Код {issue.code}
                                    </div>
                                    <span className="installer-tmc-pill">
                                        получить
                                    </span>
                                </div>
                                <div className="installer-work-card__meta">
                                    {(issue.lines || []).map(line => (
                                        <div key={line.holding_id}>
                                            {line.name} — {line.quantity}{' '}
                                            {line.unit}
                                        </div>
                                    ))}
                                </div>
                            </button>
                        ))
                    ) : (
                        <div className="installer-tmc-empty">
                            Нет выдач. Отсканируйте QR, когда склад
                            подготовит товар.
                        </div>
                    )}
                </section>

                <section className="installer-tmc-section">
                    <div className="installer-tmc-section__title">
                        У меня
                    </div>
                    {held.length ? (
                        held.map(row =>
                            renderHolding(
                                row,
                                <Button
                                    size="small"
                                    icon={<RollbackOutlined />}
                                    onClick={() => {
                                        setReturnRow(row);
                                        setReturnQty(
                                            Number(row.quantity)
                                        );
                                        setReturnNote('');
                                    }}
                                >
                                    Вернуть
                                </Button>
                            )
                        )
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="Пока ничего не числится"
                        />
                    )}
                </section>

                <section className="installer-tmc-section">
                    <div className="installer-tmc-section__title">
                        На объектах
                    </div>
                    {onObject.length ? (
                        onObject.map(row => renderHolding(row))
                    ) : (
                        <div className="installer-tmc-empty">
                            Нет ТМЦ на объектах
                        </div>
                    )}
                </section>

                {pendingReturn.length > 0 ? (
                    <section className="installer-tmc-section">
                        <div className="installer-tmc-section__title">
                            Ожидает возврата
                        </div>
                        {pendingReturn.map(row => renderHolding(row))}
                    </section>
                ) : null}

                {pendingTransfer.length > 0 ? (
                    <section className="installer-tmc-section">
                        <div className="installer-tmc-section__title">
                            Передача коллеге
                        </div>
                        {pendingTransfer.map(row => renderHolding(row))}
                        <div className="installer-tmc-empty">
                            Ждёт, пока коллега отсканирует QR. Пока
                            не уйдёт на сервер — в очереди.
                        </div>
                    </section>
                ) : null}
            </div>

            <Modal
                open={Boolean(preview)}
                onCancel={() => setPreview(null)}
                footer={null}
                title="Получение ТМЦ"
                destroyOnClose
                className="installer-tmc-sheet"
                centered
            >
                {preview ? (
                    <div>
                        <div className="installer-tmc-sheet__code">
                            {preview.code}
                        </div>
                        {(preview.lines || []).map(line => (
                            <div
                                key={line.holding_id || line.item_id}
                                className="installer-tmc-item"
                            >
                                <div className="installer-tmc-item__main">
                                    <div className="installer-tmc-item__name">
                                        {line.name}
                                    </div>
                                    <Text type="secondary">
                                        {line.quantity} {line.unit}
                                        {line.sku ? ` · ${line.sku}` : ''}
                                    </Text>
                                </div>
                            </div>
                        ))}
                        {preview.status === 'pending' ? (
                            <Button
                                type="primary"
                                size="large"
                                block
                                icon={<CheckCircleOutlined />}
                                loading={accepting}
                                disabled={!canAccept}
                                onClick={() => handleAccept(preview)}
                                style={{ marginTop: 16, height: 48 }}
                            >
                                Подтвердить получение
                            </Button>
                        ) : (
                            <Text
                                type="secondary"
                                style={{
                                    display: 'block',
                                    marginTop: 12
                                }}
                            >
                                {preview.status === 'draft'
                                    ? 'Склад ещё не оформил эту выдачу'
                                    : preview.status === 'accepted'
                                      ? 'Уже подтверждена'
                                      : 'Эту выдачу нельзя подтвердить'}
                            </Text>
                        )}
                    </div>
                ) : null}
            </Modal>

            <Modal
                title="Вернуть на склад"
                open={Boolean(returnRow)}
                onCancel={() => setReturnRow(null)}
                onOk={submitReturn}
                okText="Отправить"
                destroyOnClose
            >
                <Text>
                    {returnRow?.item?.name} — сейчас {returnRow?.quantity}{' '}
                    {returnRow?.item?.unit}
                </Text>
                <div style={{ marginTop: 12 }}>
                    <Text type="secondary">Количество</Text>
                    <InputNumber
                        min={0.001}
                        max={Number(returnRow?.quantity || 0)}
                        value={returnQty}
                        onChange={setReturnQty}
                        style={{ width: '100%', marginTop: 4 }}
                    />
                </div>
                <Input.TextArea
                    rows={2}
                    placeholder="Комментарий"
                    value={returnNote}
                    onChange={e => setReturnNote(e.target.value)}
                    style={{ marginTop: 12 }}
                />
            </Modal>

            <Modal
                title="Передать ТМЦ коллеге"
                open={transferOpen}
                onCancel={() => setTransferOpen(false)}
                onOk={createTransferQr}
                okText="Показать QR"
                destroyOnClose
                className="installer-tmc-sheet"
            >
                <Text type="secondary">
                    Отметьте, что отдаёте. Коллега сканирует QR.
                    Без интернета запись встанет в очередь и
                    останется там, пока не уйдёт на сервер.
                </Text>
                <div style={{ marginTop: 12 }}>
                    {held.map(row => (
                        <div
                            key={row.id}
                            className="installer-tmc-item"
                            style={{ alignItems: 'center' }}
                        >
                            <Checkbox
                                checked={Boolean(
                                    transferSelected[row.id]
                                )}
                                onChange={e =>
                                    setTransferSelected(prev => ({
                                        ...prev,
                                        [row.id]: e.target.checked
                                    }))
                                }
                            />
                            <div className="installer-tmc-item__main">
                                <div className="installer-tmc-item__name">
                                    {row.item?.name || '—'}
                                </div>
                                <Text type="secondary">
                                    есть {row.quantity}{' '}
                                    {row.item?.unit || ''}
                                </Text>
                            </div>
                            <InputNumber
                                min={0.001}
                                max={Number(row.quantity)}
                                value={transferQty[row.id]}
                                onChange={value =>
                                    setTransferQty(prev => ({
                                        ...prev,
                                        [row.id]: value
                                    }))
                                }
                                style={{ width: 92 }}
                            />
                        </div>
                    ))}
                </div>
            </Modal>

            <Modal
                title="Покажите QR коллеге"
                open={qrOpen}
                onCancel={() => setQrOpen(false)}
                footer={
                    <Button
                        type="primary"
                        block
                        onClick={() => setQrOpen(false)}
                    >
                        Готово
                    </Button>
                }
                destroyOnClose
                className="installer-tmc-sheet"
                centered
            >
                {qrValue ? (
                    <div style={{ textAlign: 'center' }}>
                        <QRCode
                            value={qrValue}
                            size={240}
                            errorLevel="M"
                        />
                        <Text
                            type="secondary"
                            style={{
                                display: 'block',
                                marginTop: 12
                            }}
                        >
                            Пока QR на экране, коллега сканирует
                            его в своём приложении.
                        </Text>
                    </div>
                ) : null}
            </Modal>

            <Modal
                title="Приём ТМЦ от коллеги"
                open={Boolean(incoming)}
                onCancel={() => setIncoming(null)}
                footer={null}
                destroyOnClose
                className="installer-tmc-sheet"
                centered
            >
                {incoming ? (
                    <div>
                        <Text>
                            От: {incoming.fromName || incoming.fromUserId}
                        </Text>
                        {(incoming.lines || []).map(line => (
                            <div
                                key={`${line.item_id}-${line.holding_id}`}
                                className="installer-tmc-item"
                            >
                                <div className="installer-tmc-item__main">
                                    <div className="installer-tmc-item__name">
                                        {line.name}
                                    </div>
                                    <Text type="secondary">
                                        {line.quantity} {line.unit}
                                        {line.sku
                                            ? ` · ${line.sku}`
                                            : ''}
                                    </Text>
                                </div>
                            </div>
                        ))}
                        <Button
                            type="primary"
                            size="large"
                            block
                            icon={<CheckCircleOutlined />}
                            loading={acceptingTransfer}
                            onClick={acceptIncoming}
                            style={{ marginTop: 16, height: 48 }}
                        >
                            Подтвердить получение
                        </Button>
                    </div>
                ) : null}
            </Modal>
        </Layout>
    );
};

export default InstallerTmcPage;
